import { useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { clubSidebar } from '@/config/sidebarConfig.ts';
import { createAffiliationRequestApi, getAffiliationRequestsApi } from '@/lib/api/clubsApi';
import { getOrganizationsApi } from '@/lib/api/organizationsApi';
import { getPageNumbers } from '@/lib/pagination';
import { useAuthStore } from '@/stores/auth-store';
import { formatDateAbbreviatedFr, getNameInitials } from '@shared';
import { ClubAffiliationRequestStatus } from '@shared/enums/domain.enum';
import type { ClubAffiliationRequest, Organization } from '@shared/types/entities';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Building,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Clock,
  MessageSquare,
  Plus,
  Search,
  X,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';

import DashboardLayout from '../../components/layout/DashboardLayout';
import Dialog from '../../components/ui/Dialog';
import PageHeader from '../../components/ui/PageHeader';
import QueryErrorState from '../../components/ui/QueryErrorState';
import StatCard from '../../components/ui/StatCard';

// ─── Helpers ─────────────────────────────────────────────────────
const REQ_STATUS_CONFIG: Record<
  ClubAffiliationRequestStatus,
  { label: string; bg: string; text: string; icon: typeof CheckCircle }
> = {
  [ClubAffiliationRequestStatus.PENDING]: {
    label: 'En attente',
    bg: 'bg-amber-100',
    text: 'text-amber-700',
    icon: Clock,
  },
  [ClubAffiliationRequestStatus.ACCEPTED]: {
    label: 'Acceptée',
    bg: 'bg-emerald-100',
    text: 'text-emerald-700',
    icon: CheckCircle,
  },
  [ClubAffiliationRequestStatus.REJECTED]: {
    label: 'Refusée',
    bg: 'bg-red-100',
    text: 'text-red-700',
    icon: XCircle,
  },
};

const GRADIENTS = [
  'from-blue-500 to-indigo-600',
  'from-emerald-500 to-teal-600',
  'from-purple-500 to-indigo-600',
  'from-amber-500 to-orange-600',
  'from-cyan-500 to-blue-600',
  'from-rose-500 to-indigo-600',
  'from-violet-500 to-purple-600',
  'from-teal-500 to-cyan-600',
];

function orgGradient(id: number): string {
  return GRADIENTS[id % GRADIENTS.length];
}

// ─── Send Request Modal ───────────────────────────────────────────
interface SendRequestModalProps {
  clubId: number;
  existingRequests: ClubAffiliationRequest[];
  onClose: () => void;
  onSuccess: () => void;
}

function SendRequestModal({ clubId, existingRequests, onClose, onSuccess }: SendRequestModalProps) {
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [message, setMessage] = useState('');
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Orgs already having a PENDING request
  const pendingOrgIds = useMemo(
    () =>
      new Set(
        existingRequests.filter(r => r.status === ClubAffiliationRequestStatus.PENDING).map(r => r.organizationId),
      ),
    [existingRequests],
  );

  // Orgs already affiliated (ACCEPTED)
  const acceptedOrgIds = useMemo(
    () =>
      new Set(
        existingRequests.filter(r => r.status === ClubAffiliationRequestStatus.ACCEPTED).map(r => r.organizationId),
      ),
    [existingRequests],
  );

  const { data: orgsData, isLoading: orgsLoading } = useQuery({
    queryKey: ['organizations-search', debouncedSearch],
    queryFn: () => getOrganizationsApi({ search: debouncedSearch, limit: 10 }),
    enabled: debouncedSearch.length >= 2,
  });

  const orgs = orgsData?.data ?? [];

  const sendMutation = useMutation({
    mutationFn: () =>
      createAffiliationRequestApi(clubId, {
        organizationId: selectedOrg!.id,
        message: message || undefined,
      }),
    onSuccess: () => {
      toast.success("Demande d'affiliation envoyée !");
      onSuccess();
      onClose();
    },
    onError: () => toast.error("Impossible d'envoyer la demande"),
  });

  function handleSearchChange(value: string) {
    setSearchInput(value);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => setDebouncedSearch(value), 350);
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title="Envoyer une demande d'affiliation"
      className='w-[calc(100vw-2rem)] sm:w-full max-w-2xl max-h-[85vh] overflow-hidden'
    >
      {/* Header fixe + body scroll */}
      <div className='flex max-h-[85vh] flex-col'>
        <div className='shrink-0 sticky top-0 z-10 bg-white border-b border-slate-200 px-4 sm:px-6 py-4 flex items-start justify-between'>
          <div className='min-w-0 pr-4'>
            <h3 className='text-lg font-bold text-slate-900 truncate'>Envoyer une demande d'affiliation</h3>
            <p className='text-sm text-slate-500 mt-0.5 truncate'>
              Sélectionnez une organisation puis ajoutez un message (optionnel).
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label='Fermer'
            className='shrink-0 p-2 rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-slate-600'
          >
            <X className='h-5 w-5' />
          </button>
        </div>

        <div className='flex-1 overflow-y-auto p-4 sm:p-6'>
          {!selectedOrg ? (
            <>
              <p className='mb-4 text-sm text-slate-500'>
                Recherchez une organisation pour lui envoyer une demande d&apos;affiliation.
              </p>

              <div className='relative mb-4'>
                <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400' />
                <input
                  type='text'
                  value={searchInput}
                  onChange={e => handleSearchChange(e.target.value)}
                  placeholder='Rechercher une organisation…'
                  autoFocus
                  className='w-full rounded-xl border border-slate-200 py-2.5 pl-9 pr-4 text-sm focus:border-emerald-400 focus:outline-none'
                />
              </div>

              <div className='space-y-2'>
                {debouncedSearch.length < 2 ? (
                  <p className='py-8 text-center text-sm text-slate-400'>Tapez au moins 2 caractères pour rechercher</p>
                ) : orgsLoading ? (
                  <div className='space-y-2'>
                    {Array.from({ length: 3 }).map((_, i) => (
                      <div key={i} className='h-14 animate-pulse rounded-xl bg-slate-200' />
                    ))}
                  </div>
                ) : orgs.length === 0 ? (
                  <p className='py-8 text-center text-sm text-slate-400'>Aucune organisation trouvée</p>
                ) : (
                  <ul className='space-y-1'>
                    {orgs.map(org => {
                      const isPending = pendingOrgIds.has(org.id);
                      const isAffiliated = acceptedOrgIds.has(org.id);
                      const disabled = isPending || isAffiliated;

                      return (
                        <li key={org.id}>
                          <button
                            onClick={() => !disabled && setSelectedOrg(org)}
                            disabled={disabled}
                            className={`w-full flex items-center gap-3 rounded-xl border border-transparent p-3 text-left transition ${
                              disabled
                                ? 'cursor-not-allowed opacity-50'
                                : 'hover:border-emerald-200 hover:bg-emerald-50'
                            }`}
                          >
                            <div
                              className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br ${orgGradient(
                                org.id,
                              )} text-sm font-bold text-white`}
                            >
                              {getNameInitials(org.name)}
                            </div>

                            <div className='min-w-0 flex-1'>
                              <p className='truncate text-sm font-semibold text-slate-900'>{org.name}</p>
                              <p className='truncate text-xs text-slate-400'>{org.city ?? '—'}</p>
                            </div>

                            {isPending && (
                              <span className='whitespace-nowrap rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-700'>
                                En attente
                              </span>
                            )}
                            {isAffiliated && (
                              <span className='whitespace-nowrap rounded-full bg-emerald-100 px-2 py-0.5 text-xs text-emerald-700'>
                                Affilié
                              </span>
                            )}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </>
          ) : (
            <>
              {/* Organisation sélectionnée */}
              <div className='mb-4 flex items-center gap-3 rounded-xl border border-emerald-100 bg-emerald-50 p-3'>
                <div
                  className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br ${orgGradient(
                    selectedOrg.id,
                  )} text-sm font-bold text-white`}
                >
                  {getNameInitials(selectedOrg.name)}
                </div>

                <div className='min-w-0 flex-1'>
                  <p className='truncate text-sm font-semibold text-slate-900'>{selectedOrg.name}</p>
                  {selectedOrg.city && <p className='text-xs text-slate-500'>{selectedOrg.city}</p>}
                </div>

                <button
                  onClick={() => setSelectedOrg(null)}
                  className='shrink-0 p-2 rounded-lg text-slate-400 transition hover:bg-white/60 hover:text-slate-600'
                  aria-label="Changer d'organisation"
                >
                  <X className='h-4 w-4' />
                </button>
              </div>

              <label className='mb-1 block text-sm font-medium text-slate-700'>
                Message de présentation <span className='text-slate-400'>(optionnel)</span>
              </label>

              <textarea
                value={message}
                onChange={e => setMessage(e.target.value)}
                rows={4}
                maxLength={500}
                placeholder="Présentez votre club, vos activités, vos ambitions… L'organisation pourra lire ce message avant d'accepter."
                className='mb-1 w-full resize-none rounded-xl border border-slate-200 px-4 py-3 text-sm focus:border-emerald-400 focus:outline-none'
              />

              <p className='mb-4 text-right text-xs text-slate-400'>{message.length}/500</p>

              <div className='flex flex-col sm:flex-row gap-3'>
                <button
                  onClick={() => setSelectedOrg(null)}
                  className='w-full sm:flex-1 rounded-xl border border-slate-200 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50'
                >
                  Retour
                </button>

                <button
                  onClick={() => sendMutation.mutate()}
                  disabled={sendMutation.isPending}
                  className='w-full sm:flex-1 rounded-xl bg-emerald-600 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:opacity-50'
                >
                  {sendMutation.isPending ? 'Envoi…' : 'Envoyer la demande'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </Dialog>
  );
}

// ─── Org Card (affiliation confirmée) ────────────────────────────
function OrgCard({ request }: { request: ClubAffiliationRequest }) {
  const org = request.organization;
  if (!org) return null;

  return (
    <div className='flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 transition hover:border-emerald-200 hover:shadow-lg'>
      <div className='flex items-start gap-4'>
        <div
          className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br ${orgGradient(
            org.id,
          )} text-lg font-bold text-white`}
        >
          {getNameInitials(org.name)}
        </div>

        <div className='min-w-0 flex-1'>
          <h3 className='truncate font-bold text-slate-900'>{org.name}</h3>
          {org.alias && <p className='truncate font-mono text-xs text-slate-400'>{org.alias}</p>}
        </div>
      </div>

      <div className='flex items-center gap-2 border-t border-slate-100 pt-4'>
        <CheckCircle className='h-4 w-4 shrink-0 text-emerald-500' />
        <span className='text-sm font-medium text-emerald-700'>
          Affilié depuis le {formatDateAbbreviatedFr(request.processedAt ?? request.createdAt)}
        </span>
      </div>
    </div>
  );
}

// ─── Page ────────────────────────────────────────────────────────
type TabId = 'affiliations' | 'requests';

export default function ClubOrganizers() {
  const user = useAuthStore(s => s.user);
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);
  const clubId = user?.clubId;
  const queryClient = useQueryClient();

  const [searchParams, setSearchParams] = useSearchParams();
  const tab = (searchParams.get('tab') ?? 'affiliations') as TabId;
  const reqStatusFilter = (searchParams.get('reqStatus') ?? '') as ClubAffiliationRequestStatus | '';
  const reqPage = parseInt(searchParams.get('reqPage') ?? '1', 10);
  const [showSendModal, setShowSendModal] = useState(false);

  const setParam = (key: string, value: string) => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      },
      { replace: true },
    );
  };

  // ─── Accepted affiliations ───
  const {
    data: affiliatedData,
    isLoading: affiliatedLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['affiliation-requests', { clubId, status: ClubAffiliationRequestStatus.ACCEPTED }],
    queryFn: () =>
      getAffiliationRequestsApi({
        clubId: clubId!,
        status: ClubAffiliationRequestStatus.ACCEPTED,
        limit: 100,
      }),
    enabled: !!clubId && isAuthenticated,
  });

  const affiliatedRequests = affiliatedData?.data ?? [];
  const affiliatedTotal = affiliatedData?.meta?.total ?? 0;

  // ─── All requests (for stats + modal existingRequests) ───
  const { data: allReqData } = useQuery({
    queryKey: ['affiliation-requests', { clubId, limit: 100 }],
    queryFn: () => getAffiliationRequestsApi({ clubId: clubId!, limit: 100 }),
    enabled: !!clubId && isAuthenticated,
  });

  const allRequests: ClubAffiliationRequest[] = allReqData?.data ?? [];
  const totalRequests = allReqData?.meta?.total ?? 0;

  // ─── Pending count for badge ───
  const { data: pendingData } = useQuery({
    queryKey: ['affiliation-requests', { clubId, status: ClubAffiliationRequestStatus.PENDING }],
    queryFn: () =>
      getAffiliationRequestsApi({
        clubId: clubId!,
        status: ClubAffiliationRequestStatus.PENDING,
        limit: 1,
      }),
    enabled: !!clubId && isAuthenticated,
  });
  const pendingCount = pendingData?.meta?.total ?? 0;

  // ─── Paginated requests for table ───
  const reqParams = {
    clubId: clubId!,
    ...(reqStatusFilter && { status: reqStatusFilter }),
    page: reqPage,
    limit: 15,
  };

  const { data: reqData, isLoading: reqLoading } = useQuery({
    queryKey: ['affiliation-requests', reqParams],
    queryFn: () => getAffiliationRequestsApi(reqParams),
    enabled: !!clubId && isAuthenticated,
    placeholderData: keepPreviousData,
  });

  const requests: ClubAffiliationRequest[] = reqData?.data ?? [];
  const reqTotal = reqData?.meta?.total ?? 0;
  const reqTotalPages = reqData?.meta?.totalPages ?? 1;

  function handleSendSuccess() {
    void queryClient.invalidateQueries({ queryKey: ['affiliation-requests'] });
  }

  if (isError) {
    return (
      <DashboardLayout sidebarConfig={clubSidebar}>
        <div className='p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto'>
          <QueryErrorState onRetry={refetch} />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout sidebarConfig={clubSidebar}>
      <div className='p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto'>
        <PageHeader
          title='Organisations'
          subtitle='Suivez vos affiliations et envoyez des demandes aux organisateurs'
          actions={
            <button
              onClick={() => setShowSendModal(true)}
              className='w-full sm:w-auto flex items-center justify-center gap-2 rounded-xl bg-emerald-600 px-5 py-2 text-sm font-bold text-white shadow-lg shadow-emerald-200 transition hover:bg-emerald-700'
            >
              <Plus className='h-4 w-4' />
              Nouvelle demande
            </button>
          }
        />

        {/* Stats */}
        <div className='mb-6 sm:mb-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6'>
          <StatCard
            icon={Building}
            iconBg='bg-emerald-100'
            iconColor='text-emerald-600'
            label='Organisations affiliées'
            value={affiliatedLoading ? '…' : String(affiliatedTotal)}
            subtitle='Partenaires actifs'
          />
          <StatCard
            icon={Clock}
            iconBg='bg-amber-100'
            iconColor='text-amber-600'
            label='En attente de réponse'
            value={String(pendingCount)}
            subtitle='Demandes non traitées'
          />
          <StatCard
            icon={MessageSquare}
            iconBg='bg-blue-100'
            iconColor='text-blue-600'
            label='Total des demandes'
            value={String(totalRequests)}
            subtitle='Toutes périodes confondues'
          />
        </div>

        {/* Tabs (scrollable sur mobile) */}
        <div className='mb-6 border-b border-slate-200'>
          <div className='flex gap-2 overflow-x-auto [-webkit-overflow-scrolling:touch]'>
            <button
              onClick={() => setParam('tab', '')}
              className={`shrink-0 border-b-2 px-4 sm:px-6 py-3 text-sm font-semibold transition ${
                tab === 'affiliations'
                  ? 'border-emerald-600 text-emerald-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              Organisations affiliées
              {affiliatedTotal > 0 && (
                <span className='ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600'>
                  {affiliatedTotal}
                </span>
              )}
            </button>

            <button
              onClick={() => setParam('tab', 'requests')}
              className={`shrink-0 flex items-center gap-2 border-b-2 px-4 sm:px-6 py-3 text-sm font-semibold transition ${
                tab === 'requests'
                  ? 'border-emerald-600 text-emerald-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              Mes demandes
              {pendingCount > 0 && (
                <span className='inline-flex h-5 min-w-5 px-1 items-center justify-center rounded-full bg-amber-500 text-xs font-bold text-white'>
                  {pendingCount > 99 ? '99+' : pendingCount}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* ═══════════ TAB AFFILIATIONS ═══════════ */}
        {tab === 'affiliations' && (
          <>
            {affiliatedLoading ? (
              <div className='grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-3'>
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className='h-36 animate-pulse rounded-2xl bg-slate-200' />
                ))}
              </div>
            ) : affiliatedRequests.length === 0 ? (
              <div className='flex flex-col items-center justify-center gap-4 py-16 sm:py-20 text-slate-400'>
                <Building className='h-12 w-12 opacity-30' />
                <p className='text-sm font-medium'>Aucune affiliation pour le moment</p>
                <button
                  onClick={() => setShowSendModal(true)}
                  className='w-full sm:w-auto flex items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-700'
                >
                  <Plus className='h-4 w-4' />
                  Envoyer votre première demande
                </button>
              </div>
            ) : (
              <div className='grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-3'>
                {affiliatedRequests.map(req => (
                  <OrgCard key={req.id} request={req} />
                ))}
              </div>
            )}
          </>
        )}

        {/* ═══════════ TAB REQUESTS ═══════════ */}
        {tab === 'requests' && (
          <>
            {/* Filter + Action */}
            <div className='mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3'>
              <div className='w-full overflow-x-auto'>
                <div className='inline-flex overflow-hidden rounded-lg border border-slate-200 bg-white'>
                  {(
                    [
                      { label: 'Toutes', value: '' },
                      { label: 'En attente', value: ClubAffiliationRequestStatus.PENDING },
                      { label: 'Acceptées', value: ClubAffiliationRequestStatus.ACCEPTED },
                      { label: 'Refusées', value: ClubAffiliationRequestStatus.REJECTED },
                    ] as { label: string; value: ClubAffiliationRequestStatus | '' }[]
                  ).map(f => (
                    <button
                      key={f.value}
                      onClick={() => {
                        setSearchParams(
                          prev => {
                            const next = new URLSearchParams(prev);
                            if (f.value) next.set('reqStatus', f.value);
                            else next.delete('reqStatus');
                            next.set('reqPage', '1');
                            return next;
                          },
                          { replace: true },
                        );
                      }}
                      className={`shrink-0 border-r border-slate-200 px-4 py-2.5 text-sm font-medium transition last:border-r-0 ${
                        reqStatusFilter === f.value ? 'bg-emerald-600 text-white' : 'text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
              </div>

              <button
                onClick={() => setShowSendModal(true)}
                className='w-full sm:w-auto flex items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-700'
              >
                <Plus className='h-4 w-4' />
                Nouvelle demande
              </button>
            </div>

            {/* Table (desktop) + cards (mobile) */}
            <div className='rounded-2xl border border-slate-200 bg-white overflow-hidden'>
              {/* Desktop header */}
              <div className='hidden md:grid grid-cols-12 gap-4 border-b border-slate-100 px-6 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500'>
                <div className='col-span-5'>Organisation</div>
                <div className='col-span-3'>Message envoyé</div>
                <div className='col-span-2'>Statut</div>
                <div className='col-span-2'>Date</div>
              </div>

              {/* Body */}
              {reqLoading ? (
                <>
                  {/* Mobile skeleton */}
                  <div className='md:hidden p-4 space-y-3'>
                    {Array.from({ length: 5 }).map((_, i) => (
                      <div key={i} className='h-28 animate-pulse rounded-2xl bg-slate-100' />
                    ))}
                  </div>

                  {/* Desktop skeleton */}
                  <div className='hidden md:block'>
                    {Array.from({ length: 5 }).map((_, i) => (
                      <div key={i} className='grid grid-cols-12 gap-4 border-b border-slate-50 px-6 py-4'>
                        <div className='col-span-5 h-8 animate-pulse rounded bg-slate-200' />
                        <div className='col-span-3 h-4 animate-pulse rounded bg-slate-200' />
                        <div className='col-span-2 h-4 animate-pulse rounded bg-slate-200' />
                        <div className='col-span-2 h-4 animate-pulse rounded bg-slate-200' />
                      </div>
                    ))}
                  </div>
                </>
              ) : requests.length === 0 ? (
                <div className='flex flex-col items-center justify-center gap-3 py-16 text-slate-400'>
                  <MessageSquare className='h-10 w-10 opacity-30' />
                  <p className='text-sm font-medium'>Aucune demande envoyée</p>
                </div>
              ) : (
                <>
                  {/* MOBILE: cards */}
                  <div className='md:hidden p-4 space-y-3'>
                    {requests.map(req => {
                      const cfg = REQ_STATUS_CONFIG[req.status];
                      const Icon = cfg.icon;
                      const org = req.organization;

                      return (
                        <div key={req.id} className='rounded-2xl border border-slate-200 p-4'>
                          <div className='flex items-start gap-3'>
                            <div
                              className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br ${orgGradient(
                                req.organizationId,
                              )} text-sm font-bold text-white`}
                            >
                              {org ? getNameInitials(org.name) : '?'}
                            </div>

                            <div className='min-w-0 flex-1'>
                              <p className='truncate text-sm font-semibold text-slate-900'>{org?.name ?? '—'}</p>
                              {org?.alias && <p className='truncate font-mono text-xs text-slate-400'>{org.alias}</p>}
                              {req.rejectedReason && (
                                <p className='mt-1 line-clamp-2 text-xs text-red-500'>Motif : {req.rejectedReason}</p>
                              )}
                            </div>
                          </div>

                          <div className='mt-3 flex flex-wrap items-center gap-2'>
                            <span
                              className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${cfg.bg} ${cfg.text}`}
                            >
                              <Icon className='h-3 w-3' />
                              {cfg.label}
                            </span>
                            <span className='text-xs text-slate-400'>{formatDateAbbreviatedFr(req.createdAt)}</span>
                          </div>

                          <div className='mt-3'>
                            <p className='text-xs font-semibold text-slate-500 uppercase tracking-wide'>Message</p>
                            {req.message ? (
                              <p className='mt-1 text-xs text-slate-600 line-clamp-3'>{req.message}</p>
                            ) : (
                              <p className='mt-1 text-xs italic text-slate-300'>Aucun message</p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* DESKTOP: rows */}
                  <div className='hidden md:block'>
                    {requests.map(req => {
                      const cfg = REQ_STATUS_CONFIG[req.status];
                      const Icon = cfg.icon;
                      const org = req.organization;

                      return (
                        <div
                          key={req.id}
                          className='grid grid-cols-12 items-center gap-4 border-b border-slate-50 px-6 py-4 transition hover:bg-slate-50'
                        >
                          {/* Organisation */}
                          <div className='col-span-5 flex min-w-0 items-center gap-3'>
                            <div
                              className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br ${orgGradient(
                                req.organizationId,
                              )} text-xs font-bold text-white`}
                            >
                              {org ? getNameInitials(org.name) : '?'}
                            </div>

                            <div className='min-w-0'>
                              <p className='truncate text-sm font-semibold text-slate-900'>{org?.name ?? '—'}</p>
                              {org?.alias && <p className='truncate font-mono text-xs text-slate-400'>{org.alias}</p>}
                              {req.rejectedReason && (
                                <p className='mt-0.5 line-clamp-1 text-xs text-red-500'>Motif : {req.rejectedReason}</p>
                              )}
                            </div>
                          </div>

                          {/* Message */}
                          <div className='col-span-3 min-w-0'>
                            {req.message ? (
                              <span className='line-clamp-2 text-xs text-slate-600'>{req.message}</span>
                            ) : (
                              <span className='text-xs italic text-slate-300'>Aucun message</span>
                            )}
                          </div>

                          {/* Statut */}
                          <div className='col-span-2'>
                            <span
                              className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${cfg.bg} ${cfg.text}`}
                            >
                              <Icon className='h-3 w-3' />
                              {cfg.label}
                            </span>
                          </div>

                          {/* Date */}
                          <div className='col-span-2 text-xs text-slate-400'>
                            {formatDateAbbreviatedFr(req.createdAt)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}

              {/* Pagination */}
              {reqTotal > 0 && reqTotalPages > 1 && (
                <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-t border-slate-200 px-4 sm:px-6 py-4'>
                  <p className='text-sm text-slate-500'>
                    {reqTotal} demande{reqTotal !== 1 ? 's' : ''} au total
                  </p>

                  <div className='flex items-center gap-1 flex-wrap justify-start sm:justify-end'>
                    <button
                      onClick={() => setParam('reqPage', String(Math.max(1, reqPage - 1)))}
                      disabled={reqPage === 1}
                      className='rounded-lg border border-slate-200 p-2 transition hover:bg-slate-50 disabled:opacity-40'
                      aria-label='Page précédente'
                    >
                      <ChevronLeft className='h-4 w-4' />
                    </button>

                    {getPageNumbers(reqPage, reqTotalPages).map((p, i) =>
                      p === '...' ? (
                        <span key={`d-${i}`} className='px-2 text-slate-400'>
                          …
                        </span>
                      ) : (
                        <button
                          key={p}
                          onClick={() => setParam('reqPage', String(p))}
                          aria-current={p === reqPage ? 'page' : undefined}
                          className={`rounded-lg px-3 py-1 text-sm font-semibold transition ${
                            p === reqPage ? 'bg-emerald-600 text-white' : 'border border-slate-200 hover:bg-slate-50'
                          }`}
                        >
                          {p}
                        </button>
                      ),
                    )}

                    <button
                      onClick={() => setParam('reqPage', String(Math.min(reqTotalPages, reqPage + 1)))}
                      disabled={reqPage >= reqTotalPages}
                      className='rounded-lg border border-slate-200 p-2 transition hover:bg-slate-50 disabled:opacity-40'
                      aria-label='Page suivante'
                    >
                      <ChevronRight className='h-4 w-4' />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* Send Request Modal */}
        {showSendModal && clubId && (
          <SendRequestModal
            clubId={clubId}
            existingRequests={allRequests}
            onClose={() => setShowSendModal(false)}
            onSuccess={handleSendSuccess}
          />
        )}
      </div>
    </DashboardLayout>
  );
}
